// Which desktop apps may ask for a login code. Mirrors the non-browser values
// of `user_sessions.client_type` in linq-alpha-server.
export const DESKTOP_CLIENT_TYPES = ['echo_agent', 'linq_drive'] as const
const AUTHORIZE_PATH = '/desktop/login'
const ORIGIN_PROBE = 'https://desktop-oauth-return.invalid'
const S256_CHALLENGE_PATTERN = /^[A-Za-z0-9_-]{43}$/
const SECURITY_PARAMETERS = ['client_type', 'redirect_uri', 'code_challenge', 'state'] as const

const LEGACY_AUTH_RETURN_STORAGE_KEY = 'redirect'
export const DESKTOP_OAUTH_RETURN_STORAGE_KEY = 'desktop_oauth_authorize_path'

const hasControlCharacters = (value: string): boolean =>
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0)
    return code <= 31 || code === 127
  })

const replaceControlCharacters = (value: string): string =>
  Array.from(value, (character) => {
    const code = character.charCodeAt(0)
    return code <= 31 || code === 127 ? ' ' : character
  }).join('')

const desktopStorageOrNull = (storage?: Storage): Storage | null => {
  if (storage) return storage
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export const isDesktopLoopbackRedirectUri = (value?: string | null): value is string => {
  if (!value) return false
  const match = value.match(/^http:\/\/(?:127\.0\.0\.1|localhost):([1-9]\d{0,4})\/callback$/)
  if (!match) return false
  return Number(match[1]) <= 65_535
}

export const isDesktopClientType = (value?: string | null): value is string =>
  Boolean(value) && (DESKTOP_CLIENT_TYPES as readonly string[]).includes(value as string)

export const isDesktopAuthorizeReturnPath = (path?: string | null): path is string => {
  if (!path || typeof path !== 'string') return false
  if (!path.startsWith(`${AUTHORIZE_PATH}?`) || hasControlCharacters(path)) return false

  let url: URL
  try {
    url = new URL(path, ORIGIN_PROBE)
  } catch {
    return false
  }

  if (url.origin !== ORIGIN_PROBE || url.pathname !== AUTHORIZE_PATH || url.hash) return false
  // Exactly the four contract parameters, each exactly once. An unrecognised
  // extra is rejected rather than ignored, so a stale or injected parameter
  // cannot ride along in the return path the middleware and page trust.
  let parameterCount = 0
  url.searchParams.forEach(() => {
    parameterCount += 1
  })
  if (parameterCount !== SECURITY_PARAMETERS.length) return false
  if (SECURITY_PARAMETERS.some((key) => url.searchParams.getAll(key).length !== 1)) return false

  return (
    isDesktopClientType(url.searchParams.get('client_type')) &&
    isDesktopLoopbackRedirectUri(url.searchParams.get('redirect_uri')) &&
    S256_CHALLENGE_PATTERN.test(url.searchParams.get('code_challenge') || '') &&
    Boolean(url.searchParams.get('state')?.trim())
  )
}

export const readPendingDesktopAuthorizePath = (storage?: Storage): string | null => {
  const target = desktopStorageOrNull(storage)
  if (!target) return null
  try {
    const value = target.getItem(DESKTOP_OAUTH_RETURN_STORAGE_KEY)
    return isDesktopAuthorizeReturnPath(value) ? value : null
  } catch {
    return null
  }
}

export const syncPendingDesktopAuthorizePath = (
  candidate?: string | null,
  storage?: Storage
): string | null => {
  const target = desktopStorageOrNull(storage)
  if (!target) return null

  try {
    if (isDesktopAuthorizeReturnPath(candidate)) {
      target.setItem(DESKTOP_OAUTH_RETURN_STORAGE_KEY, candidate)
      return candidate
    }

    if (readPendingDesktopAuthorizePath(target)) {
      target.removeItem(DESKTOP_OAUTH_RETURN_STORAGE_KEY)
    }
    return null
  } catch {
    return null
  }
}

export const clearPendingDesktopAuthorizePath = (
  expected?: string | null,
  storage?: Storage
): boolean => {
  const target = desktopStorageOrNull(storage)
  if (!target) return false
  try {
    const current = readPendingDesktopAuthorizePath(target)
    if (!current || (expected && current !== expected)) return false

    target.removeItem(DESKTOP_OAUTH_RETURN_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

type ReturnPathValue = string | string[] | null | undefined
type SafeReturnPathPredicate = (value?: string | null) => value is string
type OAuthProviderCallbackValue = string | string[] | undefined

type OAuthProviderCallbackAction =
  | { kind: 'wait' }
  | { kind: 'exchange'; code: string }
  | { kind: 'redirect'; destination: string }

const singleReturnPath = (value: ReturnPathValue): string | null =>
  typeof value === 'string' ? value : null

const singleNonEmptyProviderValue = (value: OAuthProviderCallbackValue): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

export const hasOAuthProviderCallback = (
  code: OAuthProviderCallbackValue,
  error: OAuthProviderCallbackValue
): boolean => Boolean(singleNonEmptyProviderValue(code) || singleNonEmptyProviderValue(error))

export const resolveProviderCallbackAction = ({
  isReady,
  code,
  error,
  storage,
}: {
  isReady: boolean
  code: OAuthProviderCallbackValue
  error: OAuthProviderCallbackValue
  storage?: Storage
}): OAuthProviderCallbackAction => {
  if (!isReady) return { kind: 'wait' }

  if (singleNonEmptyProviderValue(error)) {
    return {
      kind: 'redirect',
      destination: readPendingDesktopAuthorizePath(storage) || '/users/sign_in',
    }
  }

  const authorizationCode = singleNonEmptyProviderValue(code)
  if (authorizationCode) return { kind: 'exchange', code: authorizationCode }

  return {
    kind: 'redirect',
    destination: readPendingDesktopAuthorizePath(storage) || '/users/sign_in',
  }
}

export const resolvePostSignInReturnPath = (
  fromValue: ReturnPathValue,
  legacyStorage: Storage,
  isSafeReturnPath: SafeReturnPathPredicate,
  desktopStorage?: Storage
): string => {
  const from = singleReturnPath(fromValue)
  const desktopTarget = desktopStorageOrNull(desktopStorage)

  if (isDesktopAuthorizeReturnPath(from)) {
    if (desktopTarget) clearPendingDesktopAuthorizePath(from, desktopTarget)
    if (legacyStorage.getItem(LEGACY_AUTH_RETURN_STORAGE_KEY) === from) {
      legacyStorage.removeItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
    }
    return from
  }

  if (isSafeReturnPath(from)) {
    if (desktopTarget) clearPendingDesktopAuthorizePath(undefined, desktopTarget)
    legacyStorage.removeItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
    return from
  }

  if (desktopTarget) clearPendingDesktopAuthorizePath(undefined, desktopTarget)
  const legacy = legacyStorage.getItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
  legacyStorage.removeItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
  if (isDesktopAuthorizeReturnPath(legacy)) return '/'
  return isSafeReturnPath(legacy) ? legacy : '/'
}

export const resolveProviderCallbackReturnPath = (
  legacyStorage: Storage,
  isSafeReturnPath: SafeReturnPathPredicate,
  desktopStorage?: Storage
): string | null => {
  const pendingDesktopPath = readPendingDesktopAuthorizePath(desktopStorage)

  if (pendingDesktopPath) {
    clearPendingDesktopAuthorizePath(pendingDesktopPath, desktopStorage)
    if (legacyStorage.getItem(LEGACY_AUTH_RETURN_STORAGE_KEY) === pendingDesktopPath) {
      legacyStorage.removeItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
    }
    return pendingDesktopPath
  }

  const legacy = legacyStorage.getItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
  legacyStorage.removeItem(LEGACY_AUTH_RETURN_STORAGE_KEY)
  if (isDesktopAuthorizeReturnPath(legacy)) return null
  return isSafeReturnPath(legacy) ? legacy : null
}

export const resolveAccountCompletionReturnPath = (
  fromValue: ReturnPathValue,
  storage?: Storage
): string | null => {
  const from = singleReturnPath(fromValue)
  if (isDesktopAuthorizeReturnPath(from)) {
    const target = desktopStorageOrNull(storage)
    if (target) clearPendingDesktopAuthorizePath(from, target)
    return from
  }
  const pending = readPendingDesktopAuthorizePath(storage)
  if (pending) clearPendingDesktopAuthorizePath(pending, storage)
  return pending
}

export const sanitizeOAuthErrorDescription = (value?: string | null): string => {
  const normalized = replaceControlCharacters(value || '')
    .replace(/\s+/g, ' ')
    .trim()
  return normalized.slice(0, 200)
}

type DesktopCallbackResult =
  | { code: string; error?: never; errorDescription?: never; errorSource?: never }
  | { code?: never; error: string; errorDescription?: string; errorSource?: string }

export const buildDesktopLoopbackCallback = (
  authorizeReturnPath: string,
  result: DesktopCallbackResult
): string | null => {
  if (!isDesktopAuthorizeReturnPath(authorizeReturnPath)) return null
  const code = 'code' in result ? result.code : undefined
  const error = 'error' in result ? result.error : undefined
  if (!code && !error) return null

  const authorizeUrl = new URL(authorizeReturnPath, ORIGIN_PROBE)
  const callback = new URL(authorizeUrl.searchParams.get('redirect_uri') as string)

  if (code) {
    callback.searchParams.set('code', code)
  } else {
    callback.searchParams.set('error', error as string)
    const description = sanitizeOAuthErrorDescription(result.errorDescription)
    if (description) callback.searchParams.set('error_description', description)
    if (result.errorSource) callback.searchParams.set('error_source', result.errorSource)
  }
  callback.searchParams.set('state', authorizeUrl.searchParams.get('state') as string)
  return callback.toString()
}
